import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { useMemo } from 'react';

/**
 * GitHubRepo_GitignorePicker
 *
 * A multi-select (checkbox) for .gitignore language/framework templates. The
 * list is long, so the dropdown menu is capped in height and scrolls
 * vertically. The stored value is a `string[]` of the selected template names,
 * which the downstream Repo Foundry payload forwards unchanged.
 */

// Cap the dropdown menu height so the long template list scrolls vertically
// instead of overflowing the viewport.
const MENU_MAX_HEIGHT = 320;

export const GitignorePicker = (
  props: FieldExtensionComponentProps<string[]>,
) => {
  const { onChange, rawErrors, required, formData, schema, idSchema } = props;

  const options = useMemo<string[]>(() => {
    const items = schema?.items as { enum?: string[] } | undefined;
    return items?.enum ?? [];
  }, [schema]);

  const selected = formData ?? [];

  const hasError = rawErrors !== undefined && rawErrors.length > 0;

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string[];
    onChange(value.length > 0 ? value : undefined);
  };

  return (
    <FormControl margin="normal" required={required} error={hasError} fullWidth>
      <InputLabel id={`${idSchema?.$id}-label`}>
        {schema?.title ?? 'Languages'}
      </InputLabel>
      <Select
        labelId={`${idSchema?.$id}-label`}
        id={idSchema?.$id}
        multiple
        value={selected}
        onChange={handleChange}
        renderValue={values => (values as string[]).join(', ')}
        MenuProps={{ PaperProps: { style: { maxHeight: MENU_MAX_HEIGHT } } }}
      >
        {options.map(option => (
          <MenuItem key={option} value={option}>
            <Checkbox checked={selected.includes(option)} />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
